import { useEffect, useState } from "react";
import { Copy, Download, FileText, Lock, LogOut, MessageCircle, Share2, ShoppingCart, Smartphone, Unlock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah, fmtDate } from "../lib/api";
import { downloadVoucherPdf, voucherCopyText, voucherPdfFile } from "../lib/voucherPdf";
import { ChatPanel } from "../components/ChatPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

function LoginBox() {
  const [phone, setPhone] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/voucherchat/login", { phone, pin, device: navigator.userAgent.slice(0, 160) });
      localStorage.setItem("vc_token", data.token);
      toast.success(`Selamat datang, ${data.name}`);
      window.location.reload();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0b1020] text-white flex items-center justify-center p-4" data-testid="vc-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-6 space-y-4 shadow-xl">
        <div className="text-center space-y-1">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#F04E23] flex items-center justify-center"><MessageCircle className="w-6 h-6" /></div>
          <h1 className="text-2xl font-bold">VoucherChat Fasnet</h1>
          <p className="text-sm text-slate-300">Masuk dengan nomor WhatsApp & PIN dari admin. Pesan voucher cukup lewat chat.</p>
        </div>
        <div className="space-y-1"><label className="text-xs text-slate-300">Nomor WhatsApp</label><Input inputMode="tel" placeholder="08xxxxxxxxxx" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 text-lg bg-black/30 border-white/20" required data-testid="vc-phone" /></div>
        <div className="space-y-1"><label className="text-xs text-slate-300">PIN 6 angka</label><Input inputMode="numeric" type="password" maxLength={6} placeholder="••••••" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))} className="h-12 text-2xl tracking-[0.5em] text-center bg-black/30 border-white/20" required data-testid="vc-pin" /></div>
        <Button type="submit" disabled={busy} className="w-full h-12 text-base bg-[#F04E23] hover:bg-[#D83E15]" data-testid="vc-login-btn">{busy ? "Memeriksa…" : "Masuk"}</Button>
        <p className="text-[11px] text-slate-400 text-center">Lupa PIN / belum terdaftar? Hubungi admin Fasnet untuk didaftarkan.</p>
      </form>
    </div>
  );
}

function OrderBox({ me, onDone }) {
  const [routerId, setRouterId] = useState(me.routers[0]?.id || "");
  const [pkgs, setPkgs] = useState([]);
  const [profile, setProfile] = useState("");
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (routerId) api.get(`/voucherchat/packages/${routerId}`).then((r) => { setPkgs(r.data); setProfile(r.data[0]?.profile || ""); }).catch(() => setPkgs([])); }, [routerId]);
  const pkg = pkgs.find((p) => p.profile === profile);
  const total = (pkg?.modal || 0) * count;
  const order = async () => {
    if (!pkg) return;
    if (!window.confirm(`Buat ${count} voucher ${profile}? Saldo dipotong ${rupiah(total)}. Kode akan muncul di chat.`)) return;
    setBusy(true);
    try { const { data } = await api.post("/voucherchat/order", { router_id: routerId, profile, count }); toast.success(`${data.codes.length} voucher jadi — lihat di chat`); onDone(); }
    catch (e) { toast.error(errMsg(e)); onDone(); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-2" data-testid="vc-order">
      <div className="flex items-center gap-2 text-sm font-semibold"><ShoppingCart className="w-4 h-4 text-[#F97316]" /> Buat Voucher</div>
      {me.balance_locked ? <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-300 flex items-center gap-2" data-testid="vc-locked"><Lock className="w-4 h-4" /> Saldo Anda dikunci admin. Tulis di chat untuk minta dibuka.</div> : (
        <>
          <select value={routerId} onChange={(e) => setRouterId(e.target.value)} className="w-full h-11 rounded-md bg-black/30 border border-white/20 px-2 text-sm" data-testid="vc-router">{me.routers.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select>
          <select value={profile} onChange={(e) => setProfile(e.target.value)} className="w-full h-11 rounded-md bg-black/30 border border-white/20 px-2 text-sm" data-testid="vc-profile">{pkgs.map((p) => <option key={p.profile} value={p.profile}>{p.profile} — modal {rupiah(p.modal)}{p.jual ? ` (jual ${rupiah(p.jual)})` : ""}</option>)}</select>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" className="h-11 w-11 text-xl border-white/20" onClick={() => setCount((c) => Math.max(1, c - 1))} data-testid="vc-minus">−</Button>
            <Input type="number" min={1} max={100} value={count} onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))} className="h-11 text-center text-lg bg-black/30 border-white/20" data-testid="vc-count" />
            <Button type="button" variant="outline" className="h-11 w-11 text-xl border-white/20" onClick={() => setCount((c) => Math.min(100, c + 1))} data-testid="vc-plus">+</Button>
          </div>
          <div className="text-xs text-slate-300 flex justify-between"><span>Total modal</span><b className="text-white" data-testid="vc-total">{rupiah(total)}</b></div>
          <Button type="button" disabled={busy || !pkg || total > me.balance} onClick={order} className="w-full h-12 bg-[#F04E23] hover:bg-[#D83E15] text-base" data-testid="vc-order-btn">{busy ? "Membuat…" : total > me.balance ? "Saldo tidak cukup" : `Buat ${count} Voucher`}</Button>
        </>
      )}
    </div>
  );
}

function OrderResults({ refresh }) {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/voucherchat/orders").then((r) => { setOrders(r.data); setOpen(r.data[0]?.batch_id || null); }).catch(() => {}); }, [refresh]);
  if (!orders.length) return null;
  const toVouchers = (o) => o.codes.map((c) => ({ name: c, profile: o.profile, jual: o.jual }));
  const copy = async (o) => {
    try { await navigator.clipboard.writeText(voucherCopyText({ vouchers: toVouchers(o), profile: o.profile })); toast.success("Rincian kode disalin"); }
    catch { toast.error("Gagal menyalin"); }
  };
  const pdf = async (o) => {
    try { await downloadVoucherPdf({ vouchers: toVouchers(o), routerName: o.router_name, profile: o.profile, createdAt: o.created_at, showPrice: o.show_price !== false }); toast.success("PDF voucher diunduh"); }
    catch { toast.error("Gagal membuat PDF"); }
  };
  const share = async (o) => {
    const text = `Voucher WiFi ${o.router_name} — ${o.profile}\n${voucherCopyText({ vouchers: toVouchers(o), profile: o.profile })}\nlogin: fas.net`;
    try {
      const file = await voucherPdfFile({ vouchers: toVouchers(o), routerName: o.router_name, profile: o.profile, createdAt: o.created_at, showPrice: o.show_price !== false });
      if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: file.name, text }); return; }
    } catch (e) { if (e?.name === "AbortError") return; }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    toast.info("PDF tidak bisa dilampirkan otomatis di perangkat ini — kode dikirim sebagai teks. Unduh PDF lalu lampirkan manual bila perlu.");
  };
  return (
    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-2" data-testid="vc-results">
      <div className="flex items-center gap-2 text-sm font-semibold"><FileText className="w-4 h-4 text-emerald-400" /> Hasil Pesanan</div>
      {orders.map((o) => (
        <div key={o.batch_id} className="rounded-lg border border-white/10 bg-black/20" data-testid={`vc-result-${o.batch_id}`}>
          <button type="button" onClick={() => setOpen(open === o.batch_id ? null : o.batch_id)} className="w-full text-left px-3 py-2 text-xs flex justify-between gap-2" data-testid={`vc-result-toggle-${o.batch_id}`}>
            <span className="truncate"><b>{o.profile}</b> × {o.codes.length} — {o.router_name}</span>
            <span className="text-slate-400 shrink-0">{fmtDate(o.created_at)}</span>
          </button>
          {open === o.batch_id && (
            <div className="px-3 pb-3 space-y-2">
              <pre className="text-[11px] leading-5 font-mono whitespace-pre-wrap max-h-48 overflow-auto rounded bg-black/30 p-2" data-testid={`vc-result-codes-${o.batch_id}`}>{voucherCopyText({ vouchers: toVouchers(o), profile: o.profile })}</pre>
              <div className="grid grid-cols-2 gap-2">
                <Button type="button" variant="outline" className="h-11 border-white/20" onClick={() => copy(o)} data-testid={`vc-copy-${o.batch_id}`}><Copy className="w-4 h-4 mr-1" /> Salin Rincian</Button>
                <Button type="button" className="h-11 bg-emerald-600 hover:bg-emerald-700" onClick={() => pdf(o)} data-testid={`vc-pdf-${o.batch_id}`}><Download className="w-4 h-4 mr-1" /> Unduh PDF</Button>
              </div>
              <Button type="button" className="w-full h-11 bg-[#25D366] hover:bg-[#1ebe5b] text-black font-semibold" onClick={() => share(o)} data-testid={`vc-wa-${o.batch_id}`}><Share2 className="w-4 h-4 mr-1" /> Kirim ke WhatsApp (PDF + kode)</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function useForceDark() {
  useEffect(() => {
    const el = document.documentElement;
    const had = el.classList.contains("dark");
    el.classList.add("dark");
    const obs = new MutationObserver(() => { if (!el.classList.contains("dark")) el.classList.add("dark"); });
    obs.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => { obs.disconnect(); if (!had) el.classList.remove("dark"); };
  }, []);
}

export default function VoucherChat() {
  useForceDark();
  const [me, setMe] = useState(null);
  const [tick, setTick] = useState(0);
  const hasToken = !!localStorage.getItem("vc_token");
  const load = () => api.get("/voucherchat/me").then((r) => setMe(r.data)).catch((e) => {
    if (e?.response?.status === 401) { localStorage.removeItem("vc_token"); toast.error(errMsg(e)); }
    setMe(false);
  });
  useEffect(() => { if (hasToken) load(); }, [hasToken]); // eslint-disable-line
  useEffect(() => { if (!hasToken) return; const t = setInterval(load, 15000); return () => clearInterval(t); }, [hasToken]); // eslint-disable-line
  if (!hasToken || me === false) return <LoginBox />;
  if (!me) return <div className="min-h-screen bg-[#0b1020] text-slate-300 flex items-center justify-center text-sm">Memuat…</div>;
  const logout = () => { localStorage.removeItem("vc_token"); window.location.reload(); };
  return (
    <div className="min-h-screen bg-[#0b1020] text-white flex flex-col" data-testid="vc-page">
      <header className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#F04E23] flex items-center justify-center"><Smartphone className="w-5 h-5" /></div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate" data-testid="vc-name">{me.name} <span className="font-mono text-xs text-slate-400">{me.agent_code}</span></div>
          <div className="text-xs flex items-center gap-2">Saldo <b className={me.balance < 0 ? "text-rose-400" : "text-emerald-400"} data-testid="vc-balance">{rupiah(me.balance)}</b>
            {me.balance_locked ? <span className="inline-flex items-center gap-1 px-1.5 rounded bg-rose-500/20 text-rose-300" data-testid="vc-lock-badge"><Lock className="w-3 h-3" /> dikunci</span> : <span className="inline-flex items-center gap-1 px-1.5 rounded bg-emerald-500/20 text-emerald-300"><Unlock className="w-3 h-3" /> terbuka</span>}
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} className="text-slate-300" data-testid="vc-logout"><LogOut className="w-4 h-4" /></Button>
      </header>
      <main className="flex-1 grid lg:grid-cols-[320px_1fr] gap-3 p-3 max-w-6xl w-full mx-auto">
        <div className="space-y-3">
          <OrderBox me={me} onDone={() => { load(); setTick((t) => t + 1); }} />
          <OrderResults refresh={tick} />
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-2 min-h-[60vh]">
          <ChatPanel channel={`agent:${me.id}`} title="Chat dengan Admin Fasnet" testid="vc-chat" inputClass="text-white placeholder:text-slate-500" />
        </div>
      </main>
    </div>
  );
}
